import {
  clearElectronResources,
  deleteElectronResource,
  getResourceUrl,
  isFileProtocolRuntime,
  resourceExists,
  writeResourceFile,
} from "./mediaRuntime";

class StorageService {
  async writeFile(
    pathParts: string[],
    fileName: string,
    data: Blob | string | File | Uint8Array,
  ) {
    const electronResult = await writeResourceFile(pathParts, fileName, data);
    if (electronResult) {
      return electronResult;
    }

    const relativePath = [...pathParts, fileName].join("/");
    const bodyPayload = data instanceof Uint8Array ? data.buffer : data;
    const response = await fetch(
      `/api/upload?path=${encodeURIComponent(relativePath)}`,
      {
        method: "POST",
        body: bodyPayload as any,
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to write file: ${response.statusText}`);
    }
    return await response.json();
  }

  async exists(pathParts: string[], fileName?: string): Promise<boolean> {
    const bridgeResult = await resourceExists(pathParts, fileName);
    if (typeof bridgeResult === "boolean") {
      return bridgeResult;
    }

    if (isFileProtocolRuntime()) {
      return false;
    }

    const url = getResourceUrl(pathParts, fileName);
    return await this.checkUrl(url);
  }

  async checkUrl(url: string | undefined): Promise<boolean> {
    if (!url) return false;
    try {
      // Use cache: 'no-store' and a timestamp to bust cached 404s/index.html SPA fallbacks
      const cacheBustUrl = `${url}?t=${new Date().getTime()}`;
      const response = await fetch(cacheBustUrl, {
        method: "HEAD",
        cache: "no-store",
      });
      // Vite SPA fallback returns index.html (200 OK) for missing files, so we check the content type
      const isHtml = response.headers
        .get("content-type")
        ?.includes("text/html");
      return response.ok && !isHtml;
    } catch (err) {
      return false;
    }
  }

  async clearAll() {
    const electronResult = await clearElectronResources();
    if (typeof electronResult === "boolean") {
      return electronResult;
    }

    if (isFileProtocolRuntime()) {
      return false;
    }

    try {
      const response = await fetch("/api/upload", { method: "DELETE" });
      return response.ok;
    } catch (err) {
      console.warn("Failed to clear resources:", err);
      return false;
    }
  }

  async deletePath(pathParts: string[]): Promise<boolean> {
    const electronResult = await deleteElectronResource(pathParts);
    if (typeof electronResult === "boolean") {
      return electronResult;
    }

    if (isFileProtocolRuntime()) {
      return false;
    }

    const relativePath = pathParts.join("/");
    try {
      const response = await fetch(
        `/api/upload?path=${encodeURIComponent(relativePath)}`,
        { method: "DELETE" },
      );
      return response.ok;
    } catch (err) {
      console.warn("Failed to delete path:", err);
      return false;
    }
  }
}

export const storageService = new StorageService();
